import { isEmployerSide } from '@chat-emp/shared';

/**
 * Whether the employer still owes this channel its first reply — the state the
 * "Reply to get N credits" nudge keys on, and the gate on reporting the reward.
 *
 * Ported from stream-chat's `isFirstReplyPending` (src/utils/first-reply.ts),
 * which stream-chat-mocks also carries. Three implementations, one rule: keep
 * them identical, because the host awards against whichever one fired.
 *
 * Pending means the candidate has written and I have not written *since*. My
 * opening greeting — seeded at channel creation, before the candidate ever
 * writes — is not a reply. Once I answer, this is false for the channel
 * permanently: a later candidate message does not re-arm the reward.
 */
export interface TailMessage {
  id?: string;
  type?: string;
  user?: { id?: string } | null;
}

/** Message types that are not somebody talking, so cannot arm or spend the reward. */
const SKIPPED_TYPES = new Set(['system', 'deleted', 'ephemeral', 'error']);

/**
 * The sentinel sender the backend authors platform notes under. Exported for
 * data/stream.ts, whose rendering and unreplied-dot checks key on the same id.
 *
 * A note is not always typed `system`: `SendSystemMessage` stamps the type only
 * when the caller omits `user_id`, and "You viewed this candidate's profile."
 * ships ordinary-typed under this sender. Skipping it is one deliberate
 * addition over the port — read by type alone it counted as the candidate
 * speaking and armed the reward on a channel nobody has written in. Of the
 * sibling ports, stream-chat is being sunset and stream-chat-mocks has not
 * adopted the skip: a known divergence, accepted for now.
 */
export const SYSTEM_SENDER_ID = 'system';

export function isFirstReplyPending(
  messages: TailMessage[],
  currentUserId: string | undefined,
): boolean {
  if (!currentUserId) return false;
  let candidateMessaged = false;
  for (const message of messages) {
    if (message.type && SKIPPED_TYPES.has(message.type)) continue;
    const uid = message.user?.id;
    if (!uid || uid === SYSTEM_SENDER_ID) continue; // nobody talking — ignore
    if (!isEmployerSide(uid)) candidateMessaged = true;
    else if (candidateMessaged) return false; // I answered
  }
  return candidateMessaged;
}
